import { ComponentFactoryResolver, inject, Injectable } from '@angular/core';

import {
  NB_DOCUMENT,
  NbComponentPortal,
  NbGlobalLogicalPosition,
  NbGlobalPosition,
  NbOverlayRef,
  NbOverlayService,
  NbPositionBuilderService,
  NbPositionHelper,
} from '@nebular/theme';

import { ToastContainerComponent } from '../../toast-container/toast-container.component';
import { ToastContainer } from '../classes/toast-container.class';

type TToastOverlayAndContainer = {
  overlayRef: NbOverlayRef;
  toastrContainer: ToastContainer;
};

@Injectable()
export class ToastContainerRegistry {
  private readonly overlay = inject(NbOverlayService);

  private readonly positionBuilder = inject(NbPositionBuilderService);

  private readonly positionHelper = inject(NbPositionHelper);

  private readonly cfr = inject(ComponentFactoryResolver);

  private readonly document = inject(NB_DOCUMENT);

  protected overlays: Map<NbGlobalPosition, TToastOverlayAndContainer> = new Map();

  get(position: NbGlobalPosition): ToastContainer {
    const logicalPosition: NbGlobalLogicalPosition = this.positionHelper.toLogicalPosition(position);
    const overlayWithContainer = this.overlays.get(logicalPosition);

    if (!overlayWithContainer || !this.existsInDom(overlayWithContainer.toastrContainer)) {
      if (overlayWithContainer) {
        overlayWithContainer.overlayRef.dispose();
      }

      this.instantiateContainer(logicalPosition);
    }

    return this.overlays.get(logicalPosition)?.toastrContainer!;
  }

  protected instantiateContainer(position: NbGlobalLogicalPosition) {
    this.overlays.set(position, this.createContainer(position));
  }

  protected createContainer(position: NbGlobalLogicalPosition): TToastOverlayAndContainer {
    const positionStrategy = this.positionBuilder.global().position(position);
    const ref = this.overlay.create({ positionStrategy });

    this.addClassToOverlayHost(ref);

    const containerRef = ref.attach(new NbComponentPortal(ToastContainerComponent, null, null, this.cfr));

    return {
      overlayRef: ref,
      toastrContainer: new ToastContainer(containerRef, position, this.positionHelper),
    };
  }

  protected addClassToOverlayHost(overlayRef: NbOverlayRef) {
    overlayRef.hostElement.classList.add('toasts-overlay-container');
  }

  protected existsInDom(toastContainer: ToastContainer): boolean {
    return this.document.body.contains(toastContainer.nativeElement);
  }
}
